import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../api/api';

// Create the context
const AuthContext = createContext(null);

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  const checkAuth = useCallback(async () => {
    if (!authAPI.isAuthenticated()) {
      setLoading(false);
      return;
    }

    try {
      const userData = await authAPI.getProfile();
      setUser(userData);
      setError(null);
    } catch (err) {
      // Token invalid or expired
      authAPI.logout();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login
  const login = async (email, password, captchaToken = null) => {
    try {
      setError(null);
      await authAPI.login({ email, password, captcha_token: captchaToken });
      const userData = await authAPI.getProfile();
      setUser(userData);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Ошибка при входе';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Register
  const register = async (email, password, firstName = '', captchaToken = null) => {
    try {
      setError(null);
      const response = await authAPI.register({ 
        email, 
        password, 
        first_name: firstName,
        captcha_token: captchaToken
      });
      return { success: true, message: response.message };
    } catch (err) {
      const message = err.response?.data?.detail || 'Ошибка при регистрации';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Logout
  const logout = () => {
    authAPI.logout();
    setUser(null);
    setError(null);
  };

  // Update profile
  const updateProfile = async (data) => {
    try {
      setError(null);
      const updatedUser = await authAPI.updateProfile(data);
      setUser(updatedUser);
      return { success: true, user: updatedUser };
    } catch (err) {
      const message = err.response?.data?.detail || 'Ошибка при обновлении профиля';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      setError(null);
      const response = await authAPI.forgotPassword({ email });
      return { success: true, message: response.message };
    } catch (err) {
      const message = err.response?.data?.detail || 'Ошибка при запросе сброса пароля';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Reset password
  const resetPassword = async (token, password) => {
    try {
      setError(null);
      const response = await authAPI.resetPassword(token, { password });
      return { success: true, message: response.message };
    } catch (err) {
      const message = err.response?.data?.detail || 'Ошибка при сбросе пароля';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Verify email
  const verifyEmail = async (token) => {
    try {
      setError(null);
      const response = await authAPI.verifyEmail(token);
      return { success: true, message: response.message };
    } catch (err) {
      const message = err.response?.data?.detail || 'Ошибка при подтверждении email';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Resend verification email
  const resendVerification = async (email) => {
    try {
      setError(null);
      const response = await authAPI.resendVerification({ email });
      return { success: true, message: response.message };
    } catch (err) {
      const message = err.response?.data?.detail || 'Ошибка при отправке письма';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Refresh user data
  const refreshUser = async () => {
    try {
      const userData = await authAPI.getProfile();
      setUser(userData);
      return userData;
    } catch (err) {
      return null;
    }
  };

  // Google OAuth - redirect to Google
  const loginWithGoogle = () => {
    const googleAuthUrl = authAPI.getGoogleAuthUrl();
    window.location.href = googleAuthUrl;
  };

  // Handle Google OAuth callback
  const handleGoogleCallback = async (accessToken, refreshToken) => {
    try {
      setError(null);
      authAPI.handleGoogleCallback(accessToken, refreshToken);
      const userData = await authAPI.getProfile();
      setUser(userData);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Ошибка при входе через Google';
      setError(message);
      return { success: false, error: message };
    }
  };

  // VK OAuth - redirect to VK
  const loginWithVK = () => {
    const vkAuthUrl = authAPI.getVKAuthUrl();
    window.location.href = vkAuthUrl;
  };

  // Handle VK OAuth callback
  const handleVKCallback = async (accessToken, refreshToken) => {
    try {
      setError(null);
      authAPI.handleVKCallback(accessToken, refreshToken);
      const userData = await authAPI.getProfile();
      setUser(userData);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Ошибка при входе через VK';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Telegram Auth - get bot link
  const loginWithTelegram = async () => {
    try {
      setError(null);
      const response = await authAPI.getTelegramAuthLink();
      // Open Telegram bot link
      window.open(response.bot_link, '_blank');
      // Return token for polling (optional)
      return { success: true, token: response.token, bot_link: response.bot_link };
    } catch (err) {
      const message = err.response?.data?.detail || 'Ошибка при генерации ссылки Telegram';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Upload avatar
  const uploadAvatar = async (file) => {
    try {
      setError(null);
      const updatedUser = await authAPI.uploadAvatar(file);
      setUser(updatedUser);
      return { success: true, user: updatedUser };
    } catch (err) {
      const message = err.response?.data?.detail || 'Ошибка при загрузке аватара';
      setError(message);
      throw new Error(message);
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateProfile,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    refreshUser,
    loginWithGoogle,
    handleGoogleCallback,
    loginWithVK,
    handleVKCallback,
    loginWithTelegram,
    uploadAvatar,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

